/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import toast from "react-hot-toast";
import { Loader, Header } from "../../components";
import { useAuthContext } from "../../context/auth";
import {
  getEmpleados,
  crearEmpleado,
  actualizarEmpleado,
  getVacacionesEmpleado,
  getVacacionesPais,
  agregarVacaciones,
  getRemuneracionesEmpleado,
  crearRemuneracion,
  actualizarRemuneracion,
} from "../../utils";
import { calcularLiquidacionChile } from "../../utils";
import { getUFValue, loadExchangeRates } from "../../utils/exchangeRateService";
import { getMovimientosBancarios } from "../../utils/movimientosBancarios";
import { getPagosEmpleados, crearPagosEmpleado } from "../../utils/pagosEmpleados";
import { useCurrency } from "../../utils/useCurrency";
import styles from "./stylesEmpleados.module.scss";

// IMM Chile 2026 — usado para calcular tope gratificación en cards.
// Si se actualiza, sincronizar con src/pages/ER/hooks/useRRHHCalculations.js.
const IMM_CHILE_2026 = 539000;

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const GestionEmpleados = () => {
  const { pais, id: userId } = useAuthContext();
  const { formatAmount } = useCurrency();

  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showVacaciones, setShowVacaciones] = useState(false);
  const [showRemuneraciones, setShowRemuneraciones] = useState(false);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [vacaciones, setVacaciones] = useState([]); // del modal (1 empleado)
  const [vacacionesTodas, setVacacionesTodas] = useState([]); // para cards
  const [pagosEmpleados, setPagosEmpleados] = useState([]);
  const [showEgresados, setShowEgresados] = useState(false);
  const [remuneraciones, setRemuneraciones] = useState([]);
  const [remuneracionEditando, setRemuneracionEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    fecha_nacimiento: "",
    inicio_contrato: "",
    fin_contrato: "",
    sueldo_base: "",
    comision_porcentaje: "",
    pais,
  });
  const [vacacionForm, setVacacionForm] = useState({
    fecha_inicio: "",
    fecha_fin: "",
    notas: "",
  });
  const [showEgreso, setShowEgreso] = useState(false);
  const [egresoForm, setEgresoForm] = useState({
    egreso_fecha: "",
    egreso_monto: "",
  });
  const [remuneracionForm, setRemuneracionForm] = useState({
    inicio_vigencia: "",
    fin_vigencia: "",
    sueldo_base: "",
    comision_porcentaje: "",
    bonos_fijo: "",
    bonos_variable: "",
    notas: "",
  });
  // ─── Modal de liquidación ─────────────────────────────────
  const [showLiquidacion, setShowLiquidacion] = useState(false);
  const [liquidacionEmpleado, setLiquidacionEmpleado] = useState(null);
  const [liquidacionMes, setLiquidacionMes] = useState(new Date().getMonth() + 1);
  const [liquidacionAno, setLiquidacionAno] = useState(new Date().getFullYear());
  const [liquidacionDescuentosVarios, setLiquidacionDescuentosVarios] = useState(0);
  const [liquidacionFechaPago, setLiquidacionFechaPago] = useState("");
  const [liquidacionMovId, setLiquidacionMovId] = useState(null);
  const [liquidacionNotas, setLiquidacionNotas] = useState("");
  const [movimientosCandidatos, setMovimientosCandidatos] = useState([]);
  const [loadingMovs, setLoadingMovs] = useState(false);
  const [savingLiquidacion, setSavingLiquidacion] = useState(false);

  useEffect(() => {
    const fetchEmpleados = async () => {
      try {
        setLoading(true);
        const [empleadosData, pagosData, vacsData] = await Promise.all([
          getEmpleados(pais),
          getPagosEmpleados({ pais }),
          getVacacionesPais(pais),
        ]);
        setEmpleados(empleadosData || []);
        setPagosEmpleados(pagosData || []);
        setVacacionesTodas(vacsData || []);
      } catch (error) {
        console.error("Error al cargar empleados:", error);
        alert("Error al cargar empleados");
      } finally {
        setLoading(false);
      }
    };
    fetchEmpleados();
  }, [pais]);

  useEffect(() => {
    if (empleados.length > 0) {
      const fetchAllRemuneraciones = async () => {
        try {
          const allRem = [];
          for (const emp of empleados) {
            const data = await getRemuneracionesEmpleado(emp.id);
            allRem.push(...(data || []));
          }
          setRemuneraciones(allRem);
        } catch (error) {
          console.error("Error al cargar todas las remuneraciones:", error);
        }
      };
      fetchAllRemuneraciones();
    }
  }, [empleados]);

  // Cálculo de vacaciones — usa vacacionesTodas para no depender del modal.
  // Considera fin_contrato como tope superior. 15 días/año Chile, 30 días/año Perú.
  const calcularVacacionesEmpleado = (empleado) => {
    if (!empleado.inicio_contrato) return { generados: 0, usados: 0, disponibles: 0 };
    const inicio = new Date(empleado.inicio_contrato);
    const hasta = empleado.fin_contrato ? new Date(empleado.fin_contrato) : new Date();
    const diasPorAno = empleado.pais === "Perú" ? 30 : 15;
    const anosTrabajados = (hasta - inicio) / (1000 * 60 * 60 * 24 * 365.25);
    const diasGenerados = Math.floor(anosTrabajados * diasPorAno);
    const diasUsados = (vacacionesTodas || [])
      .filter((v) => v.empleado_id === empleado.id)
      .reduce((total, v) => {
        const ini = new Date(v.fecha_inicio);
        const fin = new Date(v.fecha_fin);
        return total + Math.ceil((fin - ini) / (1000 * 60 * 60 * 24)) + 1;
      }, 0);
    return {
      generados: diasGenerados,
      usados: diasUsados,
      disponibles: Math.max(0, diasGenerados - diasUsados),
    };
  };

  // Wrapper para mantener compatibilidad con el modal de vacaciones (state singular).
  const calcularVacaciones = (empleado) => calcularVacacionesEmpleado(empleado);

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "-";
    return new Date(fechaStr + "T12:00:00").toLocaleDateString("es", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getPagosDelEmpleado = (empleadoId) => pagosEmpleados.filter((p) => p.empleado_id === empleadoId);

  const getTotalPagado2026 = (empleadoId) =>
    pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.periodo_año === 2026)
      .reduce((sum, p) => sum + (Number(p.monto) || 0), 0);

  const getUltimoLiquido = (empleadoId) => {
    const sueldos = pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.tipo === "sueldo")
      .sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago));
    return sueldos[0]?.monto || null;
  };

  const getUltimoBono = (empleadoId) => {
    const bonos = getPagosDelEmpleado(empleadoId)
      .filter((p) => p.tipo === "bono")
      .sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago));
    return bonos[0] || null;
  };

  const getUltimaComision = (empleadoId) => {
    const coms = getPagosDelEmpleado(empleadoId)
      .filter((p) => p.tipo === "comision")
      .sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago));
    return coms[0] || null;
  };

  // Lee del state `remuneraciones` (poblado por el useEffect que recorre todos los empleados).
  const getRemuneracionVigente = (empleado) => {
    const hoy = new Date();
    const rems = (remuneraciones || [])
      .filter((r) => r.empleado_id === empleado.id)
      .filter((r) => {
        const ini = new Date(r.inicio_vigencia);
        const fin = r.fin_vigencia ? new Date(r.fin_vigencia) : null;
        return ini <= hoy && (!fin || fin >= hoy);
      })
      .sort((a, b) => new Date(b.inicio_vigencia) - new Date(a.inicio_vigencia));
    return rems[0] || null;
  };

  const monedaPais = pais === "Chile" ? "CLP" : "PEN";
  const formatMonto = (monto) => formatAmount(monto || 0, monedaPais);

  // Último período liquidado (basado en pagos_empleados, tipo 'sueldo').
  // Texto corto para el badge: "abr 2026 liquidado".
  const getMesLiquidado = (empleadoId) => {
    const sueldos = pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.tipo === "sueldo")
      .sort((a, b) => {
        if (b.periodo_año !== a.periodo_año) return b.periodo_año - a.periodo_año;
        return b.periodo_mes - a.periodo_mes;
      });
    if (!sueldos[0]) return null;
    return `${MESES[sueldos[0].periodo_mes - 1].slice(0, 3).toLowerCase()} ${sueldos[0].periodo_año} liquidado`;
  };

  const handleOpenForm = (empleado = null) => {
    if (empleado) {
      setFormData({
        ...empleado,
        fecha_nacimiento: empleado.fecha_nacimiento || "",
        fin_contrato: empleado.fin_contrato || "",
      });
      setEmpleadoSeleccionado(empleado);
    } else {
      setFormData({
        nombre: "",
        apellido: "",
        fecha_nacimiento: "",
        inicio_contrato: "",
        fin_contrato: "",
        sueldo_base: "",
        comision_porcentaje: "",
        pais,
      });
      setEmpleadoSeleccionado(null);
    }
    setShowForm(true);
  };

  const handleSaveEmpleado = async () => {
    try {
      const cleanedData = {
        nombre: formData.nombre.trim(),
        apellido: formData.apellido.trim(),
        fecha_nacimiento: formData.fecha_nacimiento || null,
        inicio_contrato: formData.inicio_contrato,
        fin_contrato: formData.fin_contrato || null,
        sueldo_base: parseFloat(formData.sueldo_base) || 0,
        comision_porcentaje: parseFloat(formData.comision_porcentaje) || 0,
        pais,
      };

      await (empleadoSeleccionado
        ? actualizarEmpleado(empleadoSeleccionado.id, cleanedData)
        : crearEmpleado(cleanedData));
      setShowForm(false);
      const data = await getEmpleados(pais);
      setEmpleados(data || []);
    } catch (error) {
      console.error("Error al guardar empleado:", error);
      alert("Error al guardar empleado");
    }
  };

  const handleOpenVacaciones = async (empleado) => {
    setEmpleadoSeleccionado(empleado);
    try {
      const data = await getVacacionesEmpleado(empleado.id);
      setVacaciones(data || []);
    } catch {
      setVacaciones([]);
    }
    setShowVacaciones(true);
  };

  const handleAddVacacion = async () => {
    try {
      await agregarVacaciones({
        empleado_id: empleadoSeleccionado.id,
        ...vacacionForm,
      });
      setVacacionForm({ fecha_inicio: "", fecha_fin: "", notas: "" });
      const data = await getVacacionesEmpleado(empleadoSeleccionado.id);
      setVacaciones(data || []);
    } catch {
      alert("Error al agregar vacaciones");
    }
  };

  const handleOpenRemuneraciones = async (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setRemuneracionEditando(null);
    setRemuneracionForm({
      inicio_vigencia: "",
      fin_vigencia: "",
      sueldo_base: "",
      comision_porcentaje: "",
      bonos_fijo: "",
      bonos_variable: "",
      notas: "",
    });
    try {
      const data = await getRemuneracionesEmpleado(empleado.id);
      setRemuneraciones(data || []);
    } catch {
      setRemuneraciones([]);
    }
    setShowRemuneraciones(true);
  };

  // ─── Modal liquidación: handlers ──────────────────────────
  // Carga egresos pendientes del país en CLP para un rango de fechas.
  // Excluye movs ya vinculados en pagos_empleados (cualquier empleado, cualquier tipo)
  // — un mismo bancario no puede ser reutilizado para varios pagos a empleados.
  const cargarMovsCandidatos = async (ano, mes) => {
    setLoadingMovs(true);
    try {
      const primerDia = new Date(ano, mes - 1, 1).toISOString().split("T")[0];
      const ultimoDia = new Date(ano, mes, 0).toISOString().split("T")[0];
      const movs = await getMovimientosBancarios(pais, {
        tipo: "egreso",
        estado: "pendiente",
        fechaDesde: primerDia,
        fechaHasta: ultimoDia,
        moneda: "CLP",
      });
      const movIdsYaUsados = new Set(
        pagosEmpleados.filter((p) => p.movimiento_bancario_id).map((p) => p.movimiento_bancario_id)
      );
      const movsFiltrados = (movs || []).filter((m) => !movIdsYaUsados.has(m.id));
      setMovimientosCandidatos(movsFiltrados);
    } catch (e) {
      console.error("Error cargando movimientos:", e);
      setMovimientosCandidatos([]);
    } finally {
      setLoadingMovs(false);
    }
  };

  const handleOpenLiquidacion = async (emp) => {
    setLiquidacionEmpleado(emp);
    const hoy = new Date();
    const mes = hoy.getMonth() + 1;
    const ano = hoy.getFullYear();
    setLiquidacionMes(mes);
    setLiquidacionAno(ano);
    setLiquidacionDescuentosVarios(0);
    setLiquidacionFechaPago("");
    setLiquidacionMovId(null);
    setLiquidacionNotas("");
    setShowLiquidacion(true);
    try {
      await loadExchangeRates();
    } catch (e) {
      console.error("Error cargando tasas:", e);
    }
    await cargarMovsCandidatos(ano, mes);
  };

  const handleLiquidacionMesChange = async (mes, ano) => {
    setLiquidacionMes(mes);
    setLiquidacionAno(ano);
    setLiquidacionMovId(null);
    await cargarMovsCandidatos(ano, mes);
  };

  const handleSaveLiquidacion = async () => {
    if (!liquidacionEmpleado || !liquidacionFechaPago) {
      toast.error("Completá la fecha de pago");
      return;
    }
    const rem = getRemuneracionVigente(liquidacionEmpleado);
    if (!rem) {
      toast.error("El empleado no tiene remuneración vigente");
      return;
    }
    const yaExiste = pagosEmpleados.some(
      (p) =>
        p.empleado_id === liquidacionEmpleado.id &&
        p.tipo === "sueldo" &&
        p.periodo_año === liquidacionAno &&
        p.periodo_mes === liquidacionMes
    );
    if (yaExiste) {
      toast.error(`Ya existe liquidación registrada para ${MESES[liquidacionMes - 1]} ${liquidacionAno}`);
      return;
    }
    try {
      setSavingLiquidacion(true);
      const ufDelMes = getUFValue(new Date(liquidacionAno, liquidacionMes - 1, 28).toISOString().split("T")[0]);
      const liq = calcularLiquidacionChile(rem, ufDelMes, Number(liquidacionDescuentosVarios) || 0);
      const row = {
        empleado_id: liquidacionEmpleado.id,
        tipo: "sueldo",
        periodo_año: liquidacionAno,
        periodo_mes: liquidacionMes,
        monto: liq.liquido,
        moneda: "CLP",
        fecha_pago: liquidacionFechaPago,
        movimiento_bancario_id: liquidacionMovId || null,
        notas: liquidacionNotas || `Liquidación ${MESES[liquidacionMes - 1]} ${liquidacionAno}`,
        created_by: userId,
      };
      await crearPagosEmpleado([row]);
      const pagosActualizados = await getPagosEmpleados({ pais });
      setPagosEmpleados(pagosActualizados || []);
      toast.success(`Liquidación de ${liquidacionEmpleado.nombre} registrada`);
      setShowLiquidacion(false);
    } catch (e) {
      console.error("Error guardando liquidación:", e);
      toast.error("Error al guardar la liquidación");
    } finally {
      setSavingLiquidacion(false);
    }
  };

  const handleOpenEgreso = (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setEgresoForm({
      egreso_fecha: empleado.egreso_fecha || "",
      egreso_monto: empleado.egreso_monto || "",
    });
    setShowEgreso(true);
  };

  const handleSaveEgreso = async () => {
    try {
      await actualizarEmpleado(empleadoSeleccionado.id, {
        egreso_fecha: egresoForm.egreso_fecha || null,
        egreso_monto: parseFloat(egresoForm.egreso_monto) || null,
      });
      setShowEgreso(false);
      const data = await getEmpleados(pais);
      setEmpleados(data || []);
      toast.success("Egreso guardado correctamente");
    } catch (error) {
      console.error("Error al guardar egreso:", error);
      toast.error("Error al guardar egreso");
    }
  };

  const handleEditRemuneracion = (rem) => {
    setRemuneracionEditando(rem);
    setRemuneracionForm({
      inicio_vigencia: rem.inicio_vigencia,
      fin_vigencia: rem.fin_vigencia || "",
      sueldo_base: rem.sueldo_base,
      comision_porcentaje: rem.comision_porcentaje,
      bonos_fijo: rem.bonos_fijo,
      bonos_variable: rem.bonos_variable,
      notas: rem.notas || "",
    });
  };

  const handleSaveRemuneracion = async () => {
    try {
      const cleanedData = {
        inicio_vigencia: remuneracionForm.inicio_vigencia,
        fin_vigencia: remuneracionForm.fin_vigencia || null,
        sueldo_base: parseFloat(remuneracionForm.sueldo_base) || 0,
        comision_porcentaje: parseFloat(remuneracionForm.comision_porcentaje) || 0,
        bonos_fijo: parseFloat(remuneracionForm.bonos_fijo) || 0,
        bonos_variable: parseFloat(remuneracionForm.bonos_variable) || 0,
        notas: remuneracionForm.notas.trim() || null,
      };

      let newRemuneraciones;
      if (remuneracionEditando) {
        const result = await actualizarRemuneracion(remuneracionEditando.id, cleanedData);
        newRemuneraciones = remuneraciones.map((r) => (r.id === remuneracionEditando.id ? result[0] : r));
      } else {
        const result = await crearRemuneracion({
          empleado_id: empleadoSeleccionado.id,
          ...cleanedData,
        });
        newRemuneraciones = [...remuneraciones, result[0]];
      }

      // Actualiza el state local del modal
      setRemuneraciones(newRemuneraciones);
      setRemuneracionEditando(null);
      setRemuneracionForm({
        inicio_vigencia: "",
        fin_vigencia: "",
        sueldo_base: "",
        comision_porcentaje: "",
        bonos_fijo: "",
        bonos_variable: "",
        notas: "",
      });

      // Recarga empleados para que la tabla principal recalcule subrows
      const data = await getEmpleados(pais);
      setEmpleados(data || []);

      // Recarga remuneraciones del empleado actual para mantener sincronizado el modal
      const updatedRem = await getRemuneracionesEmpleado(empleadoSeleccionado.id);
      setRemuneraciones(updatedRem || []);
    } catch (error) {
      console.error("Error al guardar remuneración:", error);
      alert("Error al guardar remuneración");
    }
  };

  if (loading) return <Loader />;

  // Derivado: el período seleccionado en el modal de liquidación ¿ya está registrado?
  // Si sí, el modal pasa a modo "ver" (banner verde) en lugar de "registrar".
  let yaLiquidado = null;
  if (liquidacionEmpleado) {
    yaLiquidado = pagosEmpleados.find(
      (p) =>
        p.empleado_id === liquidacionEmpleado.id &&
        p.tipo === "sueldo" &&
        p.periodo_año === liquidacionAno &&
        p.periodo_mes === liquidacionMes
    );
  }

  return (
    <div className={styles.container}>
      <Header>
        <Button variant="primary" onClick={() => handleOpenForm()} className={styles.nuevoBtn}>
          Nuevo Empleado
        </Button>
      </Header>

      <div className={styles.cardsWrapper}>
        {empleados.length === 0 ? (
          <div className="text-center" style={{ padding: 40, color: "#64748b" }}>
            No hay empleados registrados
          </div>
        ) : (
          (() => {
            const sortFn = (a, b) => {
              const fechaA = new Date(a.inicio_contrato);
              const fechaB = new Date(b.inicio_contrato);
              if (fechaA.getTime() !== fechaB.getTime()) return fechaA - fechaB;
              const sueldoA = parseFloat(a.sueldo_base) || 0;
              const sueldoB = parseFloat(b.sueldo_base) || 0;
              return sueldoB - sueldoA;
            };
            const ahora = new Date();
            const activos = [...empleados]
              .filter((e) => !e.fin_contrato || new Date(e.fin_contrato) > ahora)
              .sort(sortFn);
            const egresados = [...empleados]
              .filter((e) => e.fin_contrato && new Date(e.fin_contrato) <= ahora)
              .sort(sortFn);

            const renderCard = (emp, esEgresado) => {
              const rem = getRemuneracionVigente(emp);
              const sueldoBase = Number(rem?.sueldo_base) || Number(emp.sueldo_base) || 0;
              let gratificacion = 0;
              if (emp.pais === "Chile") {
                const gratCalc = Math.round(sueldoBase * 0.25);
                const topeMensual = Math.round((4.75 * IMM_CHILE_2026) / 12);
                gratificacion = Math.min(gratCalc, topeMensual);
              }
              const totalImponible = sueldoBase + gratificacion;
              const vacs = calcularVacacionesEmpleado(emp);
              const ultimoLiquido = getUltimoLiquido(emp.id);
              const ultimoBono = getUltimoBono(emp.id);
              const ultimaComision = getUltimaComision(emp.id);
              const totalPagado = getTotalPagado2026(emp.id);
              const segCesantia = emp.pais === "Chile" ? Math.round(totalImponible * 0.006) : 0;
              const iniciales = `${(emp.nombre || "?")[0]}${(emp.apellido || "?")[0]}`.toUpperCase();

              return (
                <div key={emp.id} className={`${styles.empleadoCard} ${esEgresado ? styles.empleadoCardEgresado : ""}`}>
                  <div className={`${styles.cardHeader} ${esEgresado ? styles.cardHeaderEgresado : ""}`}>
                    <div className={styles.cardHeaderLeft}>
                      <div className={styles.avatar}>{iniciales}</div>
                      <div>
                        <div className={styles.empNombre}>
                          {emp.nombre} {emp.apellido}
                        </div>
                        <div className={styles.empCargo}>
                          Empleado · desde {formatFecha(emp.inicio_contrato)}
                          {esEgresado && emp.fin_contrato ? ` · egreso ${formatFecha(emp.fin_contrato)}` : ""}
                        </div>
                      </div>
                    </div>
                    <div className={styles.cardHeaderRight}>
                      {getMesLiquidado(emp.id) && <span className={styles.badgeMes}>{getMesLiquidado(emp.id)}</span>}
                      {!esEgresado && (
                        <button
                          type="button"
                          className={styles.btnLiquidacion}
                          onClick={() => handleOpenLiquidacion(emp)}
                        >
                          + Liquidación
                        </button>
                      )}
                      <Button
                        size="sm"
                        variant="light"
                        onClick={() => handleOpenRemuneraciones(emp)}
                        disabled={esEgresado}
                      >
                        Remuneración
                      </Button>
                      <Button size="sm" variant="light" onClick={() => handleOpenVacaciones(emp)} disabled={esEgresado}>
                        Vacaciones
                      </Button>
                      <Button size="sm" variant="light" onClick={() => handleOpenForm(emp)} disabled={esEgresado}>
                        Datos
                      </Button>
                      {esEgresado && (
                        <Button size="sm" variant="danger" onClick={() => handleOpenEgreso(emp)}>
                          Egreso
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className={styles.cardRow}>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Sueldo base</span>
                      <span className={styles.cellValue}>{formatMonto(sueldoBase)}</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Gratificación</span>
                      <span className={styles.cellValue}>{formatMonto(gratificacion)}</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Total imponible</span>
                      <span className={styles.cellValue}>{formatMonto(totalImponible)}</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Último líquido</span>
                      <span className={styles.cellValue}>{ultimoLiquido ? formatMonto(ultimoLiquido) : "—"}</span>
                    </div>
                  </div>

                  <div className={styles.cardRow}>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>AFP</span>
                      <span className={styles.cellValue}>—</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Salud</span>
                      <span className={styles.cellValue}>—</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Seg. cesantía</span>
                      <span className={styles.cellValue}>{emp.pais === "Chile" ? formatMonto(segCesantia) : "—"}</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Impuesto único</span>
                      <span className={styles.cellValue}>—</span>
                    </div>
                  </div>

                  <div className={styles.cardRow}>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Último bono</span>
                      <span className={styles.cellValue}>{ultimoBono ? formatMonto(ultimoBono.monto) : "—"}</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Bono cédula</span>
                      <span className={styles.cellValue}>—</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Última comisión</span>
                      <span className={styles.cellValue}>
                        {ultimaComision ? formatMonto(ultimaComision.monto) : "—"}
                      </span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Anticipo</span>
                      <span className={styles.cellValue}>—</span>
                    </div>
                  </div>

                  <div className={styles.cardRow}>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Vacaciones generadas</span>
                      <span className={styles.cellValue}>{vacs.generados} días</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Vacaciones usadas</span>
                      <span className={styles.cellValue}>{vacs.usados} días</span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Saldo vacaciones</span>
                      <span className={`${styles.cellValue} ${vacs.disponibles > 0 ? styles.positive : ""}`}>
                        {vacs.disponibles} días
                      </span>
                    </div>
                    <div className={styles.cardCell}>
                      <span className={styles.cellLabel}>Total pagado 2026</span>
                      <span className={styles.cellValue}>{formatMonto(totalPagado)}</span>
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <>
                {activos.map((emp) => renderCard(emp, false))}
                {egresados.length > 0 && (
                  <>
                    <button
                      type="button"
                      className={styles.egresadosToggle}
                      onClick={() => setShowEgresados((v) => !v)}
                    >
                      {showEgresados ? "Ocultar" : "Ver"} egresados ({egresados.length})
                    </button>
                    {showEgresados && egresados.map((emp) => renderCard(emp, true))}
                  </>
                )}
              </>
            );
          })()
        )}
      </div>

      {/* Modal Formulario Empleado */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{empleadoSeleccionado ? "Editar" : "Nuevo"} Empleado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Apellido</Form.Label>
                <Form.Control
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Fecha Nacimiento</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.fecha_nacimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Inicio Contrato</Form.Label>
                <Form.Control
                  type="date"
                  required
                  value={formData.inicio_contrato}
                  onChange={(e) => setFormData({ ...formData, inicio_contrato: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Fin Contrato (opcional)</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.fin_contrato}
                  onChange={(e) => setFormData({ ...formData, fin_contrato: e.target.value || null })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Sueldo Base</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.sueldo_base}
                  onChange={(e) => setFormData({ ...formData, sueldo_base: parseFloat(e.target.value) || 0 })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Comisión %</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.comision_porcentaje}
                  onChange={(e) => setFormData({ ...formData, comision_porcentaje: parseFloat(e.target.value) || 0 })}
                />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveEmpleado}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Vacaciones */}
      <Modal show={showVacaciones} onHide={() => setShowVacaciones(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Vacaciones - {empleadoSeleccionado && `${empleadoSeleccionado.apellido} ${empleadoSeleccionado.nombre}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <h5>Agregar Período</h5>
            <div className="row">
              <Form.Group className="col-md-4">
                <Form.Label>Inicio</Form.Label>
                <Form.Control
                  type="date"
                  value={vacacionForm.fecha_inicio}
                  onChange={(e) => setVacacionForm({ ...vacacionForm, fecha_inicio: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Fin</Form.Label>
                <Form.Control
                  type="date"
                  value={vacacionForm.fecha_fin}
                  onChange={(e) => setVacacionForm({ ...vacacionForm, fecha_fin: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Notas</Form.Label>
                <Form.Control
                  value={vacacionForm.notas}
                  onChange={(e) => setVacacionForm({ ...vacacionForm, notas: e.target.value })}
                />
              </Form.Group>
              <div className="col-md-1 d-flex align-items-end">
                <Button variant="success" onClick={handleAddVacacion}>
                  +
                </Button>
              </div>
            </div>
          </div>

          <h5>Historial</h5>
          <Table striped>
            <thead>
              <tr>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Días</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {vacaciones.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    Sin registros
                  </td>
                </tr>
              ) : (
                vacaciones.map((v) => {
                  const dias =
                    Math.ceil((new Date(v.fecha_fin) - new Date(v.fecha_inicio)) / (1000 * 60 * 60 * 24)) + 1;
                  return (
                    <tr key={v.id}>
                      <td>{formatFecha(v.fecha_inicio)}</td>
                      <td>{formatFecha(v.fecha_fin)}</td>
                      <td>{dias}</td>
                      <td>{v.notas || "-"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {empleadoSeleccionado && (
            <div className="mt-3 text-end">
              <strong>Disponibles: {calcularVacaciones(empleadoSeleccionado).disponibles} días</strong>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVacaciones(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Remuneraciones */}
      <Modal show={showRemuneraciones} onHide={() => setShowRemuneraciones(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Remuneraciones - {empleadoSeleccionado && `${empleadoSeleccionado.apellido} ${empleadoSeleccionado.nombre}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4 p-3 border rounded">
            <h5>{remuneracionEditando ? "Editar" : "Agregar"} Remuneración</h5>
            <div className="row">
              <Form.Group className="col-md-3 mb-3">
                <Form.Label>Inicio Vigencia</Form.Label>
                <Form.Control
                  type="date"
                  required
                  value={remuneracionForm.inicio_vigencia}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, inicio_vigencia: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-2 mb-3">
                <Form.Label>Sueldo Base</Form.Label>
                <Form.Control
                  type="number"
                  value={remuneracionForm.sueldo_base}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, sueldo_base: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-2 mb-3">
                <Form.Label>Comisión %</Form.Label>
                <Form.Control
                  type="number"
                  value={remuneracionForm.comision_porcentaje}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, comision_porcentaje: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-2 mb-3">
                <Form.Label>Bonos Fijo</Form.Label>
                <Form.Control
                  type="number"
                  value={remuneracionForm.bonos_fijo}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, bonos_fijo: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-2 mb-3">
                <Form.Label>Bonos Variable</Form.Label>
                <Form.Control
                  type="number"
                  value={remuneracionForm.bonos_variable}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, bonos_variable: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-3 mb-3">
                <Form.Label>Notas</Form.Label>
                <Form.Control
                  value={remuneracionForm.notas}
                  onChange={(e) => setRemuneracionForm({ ...remuneracionForm, notas: e.target.value })}
                />
              </Form.Group>
              <div className="col-md-1 d-flex align-items-end mb-3">
                <Button variant="success" onClick={handleSaveRemuneracion}>
                  {remuneracionEditando ? "Actualizar" : "+"}
                </Button>
              </div>
            </div>
          </div>

          <h5>Historial de Remuneraciones</h5>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Inicio Vigencia</th>
                <th>Sueldo Base</th>
                <th>Comisión %</th>
                <th>Bonos Fijo</th>
                <th>Bonos Variable</th>
                <th>Notas</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {remuneraciones.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    Sin registros de remuneraciones
                  </td>
                </tr>
              ) : (
                remuneraciones.map((rem) => (
                  <tr key={rem.id}>
                    <td>{formatFecha(rem.inicio_vigencia)}</td>
                    <td>{formatAmount(rem.sueldo_base, pais === "Chile" ? "CLP" : "PEN")}</td>
                    <td>{rem.comision_porcentaje || 0}%</td>
                    <td>{formatAmount(rem.bonos_fijo, pais === "Chile" ? "CLP" : "PEN")}</td>
                    <td>{formatAmount(rem.bonos_variable, pais === "Chile" ? "CLP" : "PEN")}</td>
                    <td>{rem.notas || "-"}</td>
                    <td>
                      <Button size="sm" variant="outline-secondary" onClick={() => handleEditRemuneracion(rem)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemuneraciones(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal Egreso */}
      <Modal show={showEgreso} onHide={() => setShowEgreso(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Egreso - {empleadoSeleccionado && `${empleadoSeleccionado.nombre} ${empleadoSeleccionado.apellido}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Fecha de Pago</Form.Label>
              <Form.Control
                type="date"
                value={egresoForm.egreso_fecha}
                onChange={(e) => setEgresoForm({ ...egresoForm, egreso_fecha: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Monto {pais === "Chile" ? "(CLP)" : "(PEN)"}</Form.Label>
              <Form.Control
                type="number"
                value={egresoForm.egreso_monto}
                onChange={(e) => setEgresoForm({ ...egresoForm, egreso_monto: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEgreso(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveEgreso}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Liquidación */}
      <Modal show={showLiquidacion} onHide={() => setShowLiquidacion(false)} size="lg">
        <Modal.Header closeButton style={{ background: "#2c3e50", color: "#fff" }}>
          <Modal.Title style={{ fontSize: "16px" }}>
            Liquidación — {liquidacionEmpleado?.nombre} {liquidacionEmpleado?.apellido}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {liquidacionEmpleado &&
            (() => {
              const rem = getRemuneracionVigente(liquidacionEmpleado);
              if (!rem) return <p style={{ color: "#e74c3c" }}>Sin remuneración vigente</p>;
              const ufDelMes = getUFValue(new Date(liquidacionAno, liquidacionMes - 1, 28).toISOString().split("T")[0]);
              const liq = calcularLiquidacionChile(rem, ufDelMes, Number(liquidacionDescuentosVarios) || 0);
              const fmt = (n) =>
                new Intl.NumberFormat("es-CL", {
                  style: "currency",
                  currency: "CLP",
                  maximumFractionDigits: 0,
                }).format(n);
              const filas = [
                ["Sueldo base", liq.sueldoBase, false],
                rem.bonos_fijo > 0 ? ["Bono fijo", liq.bonosFijo, false] : null,
                rem.bonos_variable > 0 ? ["Bono variable", liq.bonosVariable, false] : null,
                ["Gratificación", liq.gratificacion, false],
                ["Total imponible", liq.totalImponible, true],
                [
                  `AFP ${rem.afp_nombre || ""} (cap. ${rem.afp_cap_individual_pct ?? 10}% + com. ${rem.afp_comision_pct || 0}%)`,
                  -liq.totalAfp,
                  false,
                ],
                [
                  rem.salud_tipo === "isapre"
                    ? `Isapre (UF ${rem.salud_isapre_uf} × ${ufDelMes.toFixed(2)}, piso 7%)`
                    : "Fonasa (7%)",
                  -liq.totalSalud,
                  false,
                ],
                ["Seg. cesantía (0,6%)", -liq.segCesantia, false],
                ["Base impuesto único", liq.baseImpuesto, false],
                ["Impuesto único", -liq.impuestoUnico, false],
                ["Total descuentos legales", -liq.totalDescuentosLegales, true],
              ].filter(Boolean);
              return (
                <>
                  <div style={{ display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end" }}>
                    <div>
                      <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                        Mes
                      </label>
                      <Form.Select
                        size="sm"
                        value={liquidacionMes}
                        onChange={(e) => handleLiquidacionMesChange(Number(e.target.value), liquidacionAno)}
                        style={{ width: "130px" }}
                      >
                        {MESES.map((m, i) => (
                          <option key={i} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div>
                      <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                        Año
                      </label>
                      <Form.Select
                        size="sm"
                        value={liquidacionAno}
                        onChange={(e) => handleLiquidacionMesChange(liquidacionMes, Number(e.target.value))}
                        style={{ width: "90px" }}
                      >
                        {[2024, 2025, 2026, 2027].map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>

                  <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse", marginBottom: "16px" }}>
                    <tbody>
                      {filas.map(([label, valor, bold], i) => (
                        <tr
                          key={i}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            background: bold ? "#f8fafc" : "transparent",
                          }}
                        >
                          <td
                            style={{
                              padding: "7px 8px",
                              color: "#475569",
                              fontWeight: bold ? 500 : 400,
                              textAlign: "left",
                            }}
                          >
                            {label}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              textAlign: "right",
                              fontWeight: bold ? 500 : 400,
                              color: valor < 0 ? "#e74c3c" : "#1e293b",
                            }}
                          >
                            {fmt(Math.abs(valor))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {yaLiquidado ? (
                    <div
                      style={{
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderRadius: "8px",
                        padding: "14px 16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div style={{ color: "#166534", fontSize: "13px", fontWeight: 500 }}>✓ Ya registrado</div>
                        <div style={{ color: "#166534", fontSize: "12px", marginTop: "2px" }}>
                          {new Date(yaLiquidado.fecha_pago).toLocaleDateString("es-CL")}
                          {yaLiquidado.movimiento_bancario_id ? " · vinculado al banco" : " · sin vincular al banco"}
                        </div>
                      </div>
                      <span style={{ color: "#166534", fontSize: "18px", fontWeight: 500 }}>
                        {fmt(yaLiquidado.monto)}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end" }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                            Descuentos varios (anticipos, etc.)
                          </label>
                          <Form.Control
                            type="number"
                            size="sm"
                            value={liquidacionDescuentosVarios}
                            onChange={(e) => setLiquidacionDescuentosVarios(e.target.value)}
                            min={0}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                            Fecha de pago
                          </label>
                          <Form.Control
                            type="date"
                            size="sm"
                            value={liquidacionFechaPago}
                            onChange={(e) => setLiquidacionFechaPago(e.target.value)}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          background: "#2c3e50",
                          borderRadius: "8px",
                          padding: "12px 16px",
                          marginBottom: "16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>Total líquido a pagar</span>
                        <span style={{ color: "#fff", fontSize: "20px", fontWeight: 500 }}>{fmt(liq.liquido)}</span>
                      </div>

                      <div style={{ marginBottom: "16px" }}>
                        <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                          Vincular movimiento bancario{" "}
                          {loadingMovs && <span style={{ color: "#94a3b8" }}>cargando...</span>}
                        </label>
                        <Form.Select
                          size="sm"
                          value={liquidacionMovId || ""}
                          onChange={(e) => setLiquidacionMovId(e.target.value ? Number(e.target.value) : null)}
                        >
                          <option value="">— Sin vincular —</option>
                          {movimientosCandidatos.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.fecha} · {fmt(Math.abs(m.monto))} · {(m.descripcion_raw || "").slice(0, 50)}
                            </option>
                          ))}
                        </Form.Select>
                        {movimientosCandidatos.length === 0 && !loadingMovs && (
                          <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
                            No hay egresos pendientes en {MESES[liquidacionMes - 1]} {liquidacionAno}. Podés registrar
                            sin vincular y hacerlo después desde Flujo de Caja.
                          </div>
                        )}
                      </div>

                      <div>
                        <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                          Notas
                        </label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={liquidacionNotas}
                          onChange={(e) => setLiquidacionNotas(e.target.value)}
                          placeholder={`Liquidación ${MESES[liquidacionMes - 1]} ${liquidacionAno}`}
                        />
                      </div>
                    </>
                  )}
                </>
              );
            })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowLiquidacion(false)}>
            {yaLiquidado ? "Cerrar" : "Cancelar"}
          </Button>
          {!yaLiquidado && (
            <Button
              size="sm"
              style={{ background: "#2c3e50", border: "none" }}
              onClick={handleSaveLiquidacion}
              disabled={savingLiquidacion || !liquidacionFechaPago}
            >
              {savingLiquidacion ? "Guardando..." : "Registrar liquidación"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionEmpleados;
